import { HandHeart, ChatCircleText, Feather, ShieldCheck, Quotes, Sparkle, Heart } from "@phosphor-icons/react";

const KIT_CONTENTS = [
  { title: "One intimate piece", body: "Soft, minimal, worn for a slow evening — not a performance. Designed for comfort, presence, and touch." },
  { title: "10–15 ritual cards", body: "Alternating touch and conversation prompts — half rooted in sensate focus technique, half in vulnerability. Simple, warm, never scripted." },
  { title: "A guided audio session", body: "15–20 minutes, co-created with a licensed intimacy therapist. Play it together. It leads you through the ritual, quietly." },
  { title: "The 'why' guide", body: "A short printed guide on the science of touch, oxytocin, and why couples drift. Written warmly, not clinically." },
];

const ANGLES = [
  {
    for: "For newlyweds",
    headline: "Start the way you want to continue.",
    body: "Before the routines set in, before life gets loud. A ritual that builds the intimacy habits most couples only reach for years later — quietly, without pressure.",
    accent: "Gift it for a wedding, an anniversary, or the first month home.",
  },
  {
    for: "For long-married couples",
    headline: "Find your way back to each other.",
    body: "Not a crisis intervention. Not a fix. A quiet re-entry into closeness for couples who have simply forgotten how. No judgement, no timeline, no watching eyes.",
    accent: "A single evening. Repeatable whenever you need it.",
  },
];

const TESTIMONIALS = [
  { quote: "It gave us permission to slow down. We didn't realise how much we'd been rushing until we stopped.", attribution: "Married, 12 years · Pilot participant" },
  { quote: "The audio session was the surprise. Twenty minutes, and it felt like a small room in the house we'd forgotten we owned.", attribution: "Together, 6 years · Pilot participant" },
  { quote: "I gave it as a wedding gift and got a text three weeks later that said only, 'thank you.' That was enough.", attribution: "Gifter · Pilot participant" },
];

export default function WellnessProductExtras({ product }) {
  if (product.slug !== "reconnect-ritual") return null;

  return (
    <>
      {/* Kit contents */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 border-t border-[#E6DED5] mt-16" data-testid="wellness-contents">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-[#4A1525] mb-4">What's inside</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Four objects.<br/>One quiet evening.</h2>
          </div>
          <div className="lg:col-span-8 space-y-8">
            {KIT_CONTENTS.map((c, i) => (
              <div key={i} className="flex gap-6 border-b border-[#E6DED5] pb-6 last:border-0">
                <div className="font-serif text-4xl italic text-[#4A1525] w-16 flex-shrink-0">·0{i + 1}</div>
                <div>
                  <h3 className="font-serif text-2xl mb-2">{c.title}</h3>
                  <p className="text-[#5C5C5C] leading-relaxed">{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two ways in */}
      <section className="bg-[#F0EBE1] py-24" data-testid="wellness-angles">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="overline text-[#4A1525] mb-4">Two ways in</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">One ritual. Two beginnings.</h2>
            <p className="text-[#5C5C5C] mt-6 leading-relaxed">
              Whether your marriage is three weeks or fifteen years old, the language of closeness is the same. The Ritual meets you where you are.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ANGLES.map((a, i) => (
              <div key={i} className="bg-white p-10 lg:p-12 border border-[#E6DED5]">
                {i === 0 ? <Sparkle size={28} weight="light" color="#4A1525" className="mb-6" /> : <Heart size={28} weight="light" color="#4A1525" className="mb-6" />}
                <div className="overline text-[#4A1525] mb-4">{a.for}</div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-6">{a.headline}</h3>
                <p className="text-[#5C5C5C] leading-relaxed mb-6">{a.body}</p>
                <div className="overline text-[#1A1A1A] pt-6 border-t border-[#E6DED5]">{a.accent}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20" data-testid="wellness-testimonials">
        <div className="text-center mb-16">
          <div className="overline text-[#4A1525] mb-4">Words from the pilot</div>
          <h2 className="font-serif text-4xl md:text-5xl">A quiet chorus.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="border border-[#E6DED5] p-8 bg-white flex flex-col">
              <Quotes size={28} weight="light" color="#4A1525" className="mb-6" />
              <p className="font-serif italic text-xl leading-relaxed mb-6 flex-1">"{t.quote}"</p>
              <div className="overline text-[#5C5C5C]">{t.attribution}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#1A1A1A] text-[#FAF8F5] py-16" data-testid="wellness-trust-bar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: HandHeart, title: "Touch, not performance" },
            { icon: ChatCircleText, title: "Communication first" },
            { icon: Feather, title: "Non-explicit, always" },
            { icon: ShieldCheck, title: "Discreet packaging" },
          ].map((p, i) => (
            <div key={i} className="flex flex-col items-center gap-3">
              <p.icon size={26} weight="light" color="#D9C5B2" />
              <div className="overline text-[#D9C5B2] text-[10px]">{p.title}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
